import { useState } from 'react';
import React from 'react';
import { Navigate } from 'react-router-dom';

import { AuthAlert } from '../../components/auth/AuthAlert';
import { AuthButton } from '../../components/auth/AuthButton';
import { AuthDivider } from '../../components/auth/AuthDivider';
import { AuthFooterLink } from '../../components/auth/AuthFooterLink';
import { AuthInput } from '../../components/auth/AuthInput';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { SocialLoginButton } from '../../components/auth/SocialLoginButton';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { isInAppEarlyAccessEnabled } from '../../services/earlyAccess';
import { handleGoogleLogin } from '../../services/oauth';
import { userReducer } from '../../store/reducers/user.reducer';
import { getRegisterErrorMessage } from '../../store/selectors/user.selector';

const { register } = userReducer.actions;

// While registration is invite-only, /register opens the early access form on the login page.
export const RegisterPage = () =>
  isInAppEarlyAccessEnabled() ? <Navigate to="/login?subscribe=1" replace /> : <RegisterForm />;

const RegisterForm = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');

  const dispatch = useAppDispatch();
  const registerErrorMessage = useAppSelector(getRegisterErrorMessage);

  const validateForm = () => {
    let isValid = true;
    if (!fullName.trim()) {
      setNameError('Please enter your full name');
      isValid = false;
    } else setNameError('');
    if (!email) {
      setEmailError('Please provide your email');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    } else setEmailError('');
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      isValid = false;
    } else if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      isValid = false;
    } else setPasswordError('');
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    dispatch(register({ fullName, email, password }));
  };

  return (
    <AuthLayout title="Sign up" subtitle="Please provide your email and password.">
      <SocialLoginButton provider="google" onClick={handleGoogleLogin} />

      <AuthDivider />

      {registerErrorMessage && <AuthAlert type="error" message={registerErrorMessage} />}

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <AuthInput
          label="Full Name"
          value={fullName}
          onChange={setFullName}
          error={nameError}
          autoComplete="name"
          autoFocus
          name="fullName"
        />

        <AuthInput
          label="Email Address"
          type="email"
          value={email}
          onChange={setEmail}
          error={emailError}
          autoComplete="email"
          name="email"
        />

        <AuthInput
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          error={passwordError}
          helperText="Password must be at least 8 characters"
          autoComplete="new-password"
          name="password"
        />

        <AuthInput
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={passwordError ? ' ' : undefined}
          autoComplete="new-password"
          name="confirmPassword"
        />

        <label className="flex items-center gap-1.5 text-xs text-greyP2 cursor-pointer">
          <input type="checkbox" id="agree-terms" className="w-3.5 h-3.5 accent-green01" />I agree to join Taman's
          mailing list
        </label>

        <AuthButton type="submit">Create account</AuthButton>
      </form>

      <AuthFooterLink text="Already have an account?" linkText="Log in" to="/login" />
    </AuthLayout>
  );
};
